import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CtmsWorkspaceModal from "../../../common/ctms/CtmsWorkspaceModal";
import Dropdown from "../../../common/dropDown";
import GenderSelector from "../../../common/GenderSelector";
import SimpleCircularUpload from "../../../common/SimpleCircularUpload";
import { useLoader } from "../../../../context/LoaderContext";
import useInfoAlert from "../../../../hooks/useInfoAlert";
import InfoAlert from "../../../common/InfoAlert";
import { getInternalActiveStaffLookup } from "../../../../services/lookupService";
import MilestoneRecipientStaff from "../../isf/MilestoneRecipientStaff";
import {
  fetchActiveVolunteers,
  getVolunteerDisplayName,
  getVolunteerRowId,
} from "../../participantmanager/participantService";
import {
  fetchOrganizationSetup,
  parseOrganizationSetupResponse,
} from "../../../../services/organizationSetupService";
import {
  normalizeNationalMobileInput,
  orgMobileDialLabel,
  sanitizeNationalMobileInput,
  validateNationalMobile,
} from "../../../../utils/siteFieldValidationUtils";
import {
  bookAppointmentRequest,
  fetchPlannerDefaultSetup,
  fetchPlannerGroupsAndVisits,
  fetchPlannerStudyBundle,
  fetchStudyAssignedStaff,
  fetchStudyVisitWindowMeta,
  fetchVolunteerStudyAppointments,
  fetchWindowAppointments,
  appointmentNetworkErrorMessage,
  parseAppointmentActivityResult,
} from "../eplannerService";
import {
  buildSmartTentativeWindowRows,
  formatWindowAppointmentRow,
  findOptionByValue,
  getWindowVisitExpectedDateIso,
  mapGroupOptions,
  mapStudyOptions,
  mapVisitOptions,
  isEplannerGuid,
  mergeApiAndSmartWindowRows,
  resolveVolunteerGuid,
  toBookingDateIso,
} from "../eplannerReportUtils";
import { useEplannerFilters } from "../EplannerLayout";
import AppointmentLocationPicker from "./AppointmentLocationPicker";
import AppointmentTimePicker, {
  getNextAvailableTime,
  isPastSlot,
  isSlotOccupied,
  nextFreeSlotTime,
  resolveTimeIntervalMinutes,
  todayIsoDate,
} from "./AppointmentTimePicker";

export const BOOK_MODES = {
  BLANK: "blank",
  PREFILL_VOLUNTEER: "prefillVolunteer",
  CONTEXT: "context",
};

const APPOINTMENT_TYPE_OPTIONS = [
  { value: "Clinic", label: "Clinic" },
  { value: "Virtual", label: "Virtual" },
  { value: "Telephone", label: "Telephone" },
];

function formatGenderDisplay(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key === "m" || key === "male") return "Male";
  if (key === "f" || key === "female") return "Female";
  if (key === "o" || key === "other") return "Other";
  return value ? String(value) : "";
}

function parseIsoDateOnly(value) {
  const match = String(value || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    0,
    0,
    0,
    0,
  );
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatIsoDateOnly(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const EMPTY_FORM = {
  volunteerName: "",
  firstName: "",
  middleName: "",
  lastName: "",
  hasMiddleName: false,
  photoUrl: "",
  photoBase64: "",
  mobile: "",
  mobileCountryIso2: "GB",
  dob: "",
  gender: "",
  volunteerIdDisplay: "",
  email: "",
  altEmail: "",
  landline: "",
  kinName: "",
  kinContact: "",
  studyId: "",
  groupId: "",
  visitId: "",
  adhocReason: "",
  appointmentType: "Clinic",
  screeningId: "",
  randomisedId: "",
  verifyScreeningId: false,
  verifyRandomisedId: false,
  appointmentDate: "",
  appointmentTime: "",
  locationId: "",
  buildingId: "",
  floorId: "",
  roomId: "",
  roomTypeId: "",
  locationType: "",
  locationLabel: "",
  reason: "",
  staffIds: [],
};

const EMPTY_LOCATION = {
  locationId: "",
  buildingId: "",
  floorId: "",
  roomId: "",
  roomTypeId: "",
  locationType: "",
  locationLabel: "",
};

function resolveMobileCountryIso2(row, orgCountryIso2 = "GB") {
  return (
    String(
      row?.MobileCountryIso2 ||
        row?.mobileCountryIso2 ||
        orgCountryIso2 ||
        "GB",
    )
      .trim()
      .toUpperCase() || "GB"
  );
}

function formatMobileDisplay(national, countryIso2 = "GB") {
  const iso = resolveMobileCountryIso2(null, countryIso2);
  const digits =
    sanitizeNationalMobileInput(national, iso) ||
    String(national || "").replace(/\D/g, "");
  if (!digits) return "";
  return `${orgMobileDialLabel(iso)} ${digits}`;
}

function pickVolunteerField(row, keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && String(value).trim() !== "") return String(value).trim();
  }
  return "";
}

function volunteerIdOf(row) {
  // Prefer GUID (VolIdRef). Fast list sets VolunteerId to the display number (e.g. "189").
  const fromRow = resolveVolunteerGuid(row);
  if (fromRow) return fromRow;
  const demId = getVolunteerRowId(row);
  return isEplannerGuid(demId) ? demId : "";
}

function toLegacyStartTime(dateIso, timeIso) {
  const [year, month, day] = String(dateIso || "").split("-");
  const [hour = "00", minute = "00"] = String(timeIso || "").split(":");
  if (!year || !month || !day) return "";
  return `${day}/${month}/${year} ${hour.padStart(2, "0")}:${minute.padStart(2, "0")}:00`;
}

function dobToInput(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const match = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (match) {
    const [, d, m, y] = match;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, "0");
    const d = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return "";
}

function dobToApi(value) {
  const text = String(value || "").trim();
  if (!text) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    const [y, m, d] = text.split("-");
    return `${m}/${d}/${y}`;
  }
  return text;
}

/** SQL uniqueidentifier params reject ""; use null instead. */
function guidOrNull(value) {
  const text = String(value ?? "").trim();
  if (!text) return null;
  if (
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      text,
    )
  ) {
    return null;
  }
  return text;
}

function formatDobDisplay(value) {
  const iso = dobToInput(value);
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return String(value || "—");
  return `${d}/${m}/${y}`;
}

function composeVolunteerName(firstName, middleName, lastName) {
  return [firstName, middleName, lastName]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
}

function splitVolunteerName(fullName) {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return { firstName: "", middleName: "", lastName: "" };
  if (parts.length === 1) {
    return { firstName: parts[0], middleName: "", lastName: "" };
  }
  if (parts.length === 2) {
    return { firstName: parts[0], middleName: "", lastName: parts[1] };
  }
  return {
    firstName: parts[0],
    middleName: parts.slice(1, -1).join(" "),
    lastName: parts[parts.length - 1],
  };
}

function volunteerHasMiddleNameInDb(row) {
  if (!row || typeof row !== "object") return false;
  const keys = ["MiddleName", "middleName", "Middle", "SecondName"];
  return keys.some((key) =>
    Object.prototype.hasOwnProperty.call(row, key)
      ? String(row[key] ?? "").trim() !== ""
      : false,
  );
}

function applyVolunteerToForm(row, orgCountryIso2 = "GB") {
  if (!row) return {};
  const mobileCountryIso2 = resolveMobileCountryIso2(row, orgCountryIso2);
  const rawMobile = pickVolunteerField(row, [
    "MobileNo",
    "ContactNumber",
    "VolMobile",
    "mobileNo",
  ]);
  const firstName = pickVolunteerField(row, [
    "FirstName",
    "ForeName",
    "firstName",
    "foreName",
  ]);
  const middleName = pickVolunteerField(row, [
    "MiddleName",
    "middleName",
    "Middle",
    "SecondName",
  ]);
  const lastName = pickVolunteerField(row, [
    "LastName",
    "SurName",
    "lastName",
    "surName",
  ]);
  const composed = composeVolunteerName(firstName, middleName, lastName);
  const fallbackName =
    pickVolunteerField(row, ["VolName", "volName", "VolunteerName", "Name"]) ||
    getVolunteerDisplayName(row);
  const name =
    composed || (fallbackName === "—" ? "" : fallbackName);
  const split = !composed && name ? splitVolunteerName(name) : null;
  const volDisplay = pickVolunteerField(row, [
    "VolunteerNo",
    "VolId",
    "VolunteerNumber",
    "volId",
  ]);
  return {
    volunteerName: name,
    firstName: firstName || split?.firstName || "",
    middleName: middleName || split?.middleName || "",
    lastName: lastName || split?.lastName || "",
    hasMiddleName: volunteerHasMiddleNameInDb(row) || Boolean(middleName),
    photoUrl: pickVolunteerField(row, [
      "ProfilePictureUrl",
      "PhotoUrl",
      "photoUrl",
      "VolPhoto",
      "VolunteerPhoto",
      "ImageUrl",
    ]),
    photoBase64: "",
    mobile:
      sanitizeNationalMobileInput(rawMobile, mobileCountryIso2) ||
      rawMobile.replace(/\D/g, "") ||
      rawMobile,
    mobileCountryIso2,
    dob: dobToInput(
      pickVolunteerField(row, ["DOB", "Dob", "DateOfBirth", "dob"]),
    ),
    gender: pickVolunteerField(row, ["Gender", "gender"]),
    volunteerIdDisplay: volDisplay,
    email: pickVolunteerField(row, [
      "EmailId",
      "WEmailId",
      "Email",
      "emailId",
    ]),
    altEmail: pickVolunteerField(row, ["PEmailId", "AltEmail", "pEmailId"]),
    landline: pickVolunteerField(row, [
      "LandLineNo",
      "VolLandLineNo",
      "Landline",
      "landLineNo",
    ]),
    kinName: pickVolunteerField(row, ["KinName", "kinName"]),
    kinContact: pickVolunteerField(row, ["KinContactNo", "kinContactNo"]),
    screeningId: pickVolunteerField(row, ["ScreeningId", "screeningId"]),
    randomisedId: pickVolunteerField(row, [
      "RdId",
      "RandomisedId",
      "RandomId",
      "rdId",
    ]),
    verifyScreeningId: (() => {
      const raw =
        row?.IsScreeningIdVerify ??
        row?.isScreeningIdVerify ??
        row?.VerifyScreeningId;
      return raw === true || raw === "true" || raw === 1 || raw === "1";
    })(),
    verifyRandomisedId: (() => {
      const raw =
        row?.IsRandomisedIdVerify ??
        row?.isRandomisedIdVerify ??
        row?.VerifyRandomisedId;
      return raw === true || raw === "true" || raw === 1 || raw === "1";
    })(),
    studyId: pickVolunteerField(row, [
      "StudyIdRef",
      "StudyScheduleId",
      "StudyId",
      "studyIdRef",
    ]),
  };
}

function sameVolunteer(a, b) {
  if (!a || !b) return false;
  const idA = volunteerIdOf(a);
  const idB = volunteerIdOf(b);
  if (idA && idB && idA === idB) return true;
  const noA = pickVolunteerField(a, ["VolunteerNo", "VolId"]);
  const noB = pickVolunteerField(b, ["VolunteerNo", "VolId"]);
  return Boolean(noA && noB && noA === noB);
}

function Field({ label, required, children, className = "", error = "" }) {
  return (
    <div className={`eplanner-book__field ${className}`.trim()}>
      <span className="eplanner-book__label">
        {label}
        {required ? <em aria-hidden="true">*</em> : null}
      </span>
      {children}
      {error ? (
        <span className="eplanner-book__field-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

function useInlineEditor() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return { open, setOpen, draft, setDraft, wrapRef };
}

function InlineEditActions({ onCancel, onSave }) {
  return (
    <div className="eplanner-book__inline-actions">
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        onClick={onCancel}
      >
        Cancel
      </button>
      <button type="button" className="btn btn-sm btn-primary" onClick={onSave}>
        Save
      </button>
    </div>
  );
}

function ProfileFact({
  icon,
  label,
  displayValue,
  inputType = "text",
  draftValue,
  onSave,
  maxLength,
  editor = "text",
  countryIso2 = "GB",
}) {
  const { open, setOpen, draft, setDraft, wrapRef } = useInlineEditor();
  const [fieldError, setFieldError] = useState("");
  const shown = String(displayValue || "").trim();
  const empty = !shown;
  const isPhone = editor === "phone";
  const dialLabel = orgMobileDialLabel(countryIso2);

  const openEditor = () => {
    const raw = draftValue != null ? String(draftValue) : empty ? "" : shown;
    const national = isPhone
      ? sanitizeNationalMobileInput(raw, countryIso2) ||
        raw.replace(/\D/g, "")
      : raw;
    setDraft(national);
    setFieldError("");
    setOpen(true);
  };

  const save = () => {
    if (isPhone) {
      const result = validateNationalMobile(draft, {
        countryIso2,
        required: true,
      });
      if (!result.valid) {
        setFieldError(result.message);
        return;
      }
      onSave?.(result.national, { mobileCountryIso2: countryIso2 });
      setOpen(false);
      setFieldError("");
      return;
    }
    onSave?.(draft);
    setOpen(false);
    setFieldError("");
  };

  return (
    <div
      className={`eplanner-book__fact${empty ? " is-empty" : ""}${open ? " is-editing" : ""}`}
      ref={wrapRef}
    >
      <span className="eplanner-book__fact-icon" aria-hidden="true">
        <i className={icon} />
      </span>
      <div className="eplanner-book__fact-body">
        <span className="eplanner-book__fact-label">{label}</span>
        {open ? (
          <div className="eplanner-book__inline-edit">
            {isPhone ? (
              <div className="eplanner-book__phone-input">
                <span className="eplanner-book__phone-input__dial" aria-hidden="true">
                  {dialLabel}
                </span>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={draft}
                  onChange={(event) => {
                    const { national, wrongDial } = normalizeNationalMobileInput(
                      event.target.value,
                      countryIso2,
                    );
                    setDraft(
                      national ||
                        (wrongDial ? "" : event.target.value.replace(/\D/g, "")),
                    );
                    setFieldError("");
                  }}
                  onPaste={(event) => {
                    const text = event.clipboardData?.getData("text") ?? "";
                    event.preventDefault();
                    const { national } = normalizeNationalMobileInput(
                      text,
                      countryIso2,
                    );
                    setDraft(national);
                    setFieldError("");
                  }}
                  autoFocus
                  inputMode="numeric"
                  placeholder="7123456789"
                  aria-label={`Mobile number ${dialLabel}`}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      save();
                    }
                    if (event.key === "Escape") setOpen(false);
                  }}
                />
              </div>
            ) : (
              <input
                type={inputType}
                className="form-control form-control-sm"
                value={draft}
                maxLength={maxLength}
                onChange={(event) => setDraft(event.target.value)}
                autoFocus
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    save();
                  }
                  if (event.key === "Escape") setOpen(false);
                }}
              />
            )}
            {fieldError ? (
              <span className="eplanner-book__field-error" role="alert">
                {fieldError}
              </span>
            ) : null}
            <InlineEditActions onCancel={() => setOpen(false)} onSave={save} />
          </div>
        ) : (
          <span className="eplanner-book__fact-value" title={shown || undefined}>
            {empty ? "Not set" : shown}
          </span>
        )}
      </div>
      {!open ? (
        <button
          type="button"
          className="eplanner-book__pencil-btn"
          title={`Edit ${label}`}
          aria-label={`Edit ${label}`}
          onClick={openEditor}
        >
          <i className="fas fa-pencil-alt" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

function ProfileGenderFact({ value, onChange }) {
  const [editOpen, setEditOpen] = useState(false);
  const wrapRef = useRef(null);
  const display = formatGenderDisplay(value);
  const empty = !display;

  useEffect(() => {
    if (!editOpen) return undefined;
    const onDoc = (event) => {
      if (!wrapRef.current?.contains(event.target)) setEditOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [editOpen]);

  return (
    <div
      className={`eplanner-book__fact eplanner-book__fact--gender${
        empty ? " is-empty" : ""
      }${editOpen ? " is-editing" : ""}`}
      ref={wrapRef}
    >
      <span className="eplanner-book__fact-icon" aria-hidden="true">
        <i className="fas fa-venus-mars" />
      </span>
      <div className="eplanner-book__fact-body">
        <span className="eplanner-book__fact-label">Gender</span>
        {editOpen ? (
          <div className="eplanner-book__gender-edit">
            <GenderSelector
              value={value || ""}
              onChange={(next) => {
                onChange?.(next || "");
                setEditOpen(false);
              }}
              size="small"
            />
          </div>
        ) : (
          <span className="eplanner-book__fact-value">
            {empty ? "Not set" : display}
          </span>
        )}
      </div>
      {!editOpen ? (
        <button
          type="button"
          className="eplanner-book__pencil-btn"
          title="Edit gender"
          aria-label="Edit gender"
          onClick={() => setEditOpen(true)}
        >
          <i className="fas fa-pencil-alt" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

function ProfileNameHero({ form, onSave }) {
  const { open, setOpen, wrapRef } = useInlineEditor();
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const display =
    form.volunteerName ||
    composeVolunteerName(form.firstName, form.middleName, form.lastName) ||
    "Add name";

  const openEditor = () => {
    setFirstName(form.firstName || "");
    setMiddleName(form.middleName || "");
    setLastName(form.lastName || "");
    setOpen(true);
  };

  const save = () => {
    onSave?.({
      firstName: firstName.trim(),
      middleName: middleName.trim(),
      lastName: lastName.trim(),
    });
    setOpen(false);
  };

  return (
    <div
      className={`eplanner-book__profile-name${open ? " is-editing" : ""}`}
      ref={wrapRef}
    >
      {open ? (
        <div className="eplanner-book__name-inline-edit">
          <label className="eplanner-book__inline-field">
            <span>First name</span>
            <input
              type="text"
              className="form-control form-control-sm"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              autoFocus
            />
          </label>
          {form.hasMiddleName ? (
            <label className="eplanner-book__inline-field">
              <span>Middle name</span>
              <input
                type="text"
                className="form-control form-control-sm"
                value={middleName}
                onChange={(event) => setMiddleName(event.target.value)}
              />
            </label>
          ) : null}
          <label className="eplanner-book__inline-field">
            <span>Last name</span>
            <input
              type="text"
              className="form-control form-control-sm"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
          </label>
          <InlineEditActions onCancel={() => setOpen(false)} onSave={save} />
        </div>
      ) : (
        <>
          <h4 title={display}>{display}</h4>
          <button
            type="button"
            className="eplanner-book__pencil-btn"
            title="Edit full name"
            aria-label="Edit full name"
            onClick={openEditor}
          >
            <i className="fas fa-pencil-alt" aria-hidden="true" />
          </button>
        </>
      )}
    </div>
  );
}

function ParticipantDetailsPanel({ form, onPatchForm, orgCountryIso2 = "GB" }) {
  const mobileCountryIso2 = resolveMobileCountryIso2(form, orgCountryIso2);
  return (
    <div className="eplanner-book__info-card eplanner-book__info-card--profile">
      <div className="eplanner-book__profile-hero">
        <div className="eplanner-book__profile-avatar-wrap">
          <SimpleCircularUpload
            defaultImage={null}
            currentImage={form.photoUrl || null}
            size={72}
            maxFileSize={5}
            onChange={(base64) =>
              onPatchForm({
                photoUrl: base64 || "",
                photoBase64: base64 || "",
              })
            }
          />
        </div>

        <div className="eplanner-book__profile-volno">
          {form.volunteerIdDisplay || "No participant no."}
        </div>

        <ProfileNameHero
          form={form}
          onSave={({ firstName, middleName, lastName }) => {
            onPatchForm({
              firstName,
              middleName,
              lastName,
              volunteerName: composeVolunteerName(
                firstName,
                middleName,
                lastName,
              ),
            });
          }}
        />
      </div>

      <div className="eplanner-book__profile-group">
        <span className="eplanner-book__profile-group-label">Contact</span>
        <div className="eplanner-book__fact-grid">
          <ProfileFact
            icon="fas fa-mobile-alt"
            label="Mobile"
            displayValue={formatMobileDisplay(form.mobile, mobileCountryIso2)}
            draftValue={form.mobile}
            editor="phone"
            countryIso2={mobileCountryIso2}
            onSave={(value, extra) =>
              onPatchForm({
                mobile: String(value || "").trim(),
                mobileCountryIso2:
                  extra?.mobileCountryIso2 || mobileCountryIso2,
              })
            }
          />
          <ProfileFact
            icon="fas fa-envelope"
            label="Email"
            displayValue={form.email}
            draftValue={form.email}
            inputType="email"
            onSave={(value) =>
              onPatchForm({ email: String(value || "").trim() })
            }
          />
          <ProfileFact
            icon="fas fa-at"
            label="Alternate email"
            displayValue={form.altEmail}
            draftValue={form.altEmail}
            inputType="email"
            onSave={(value) =>
              onPatchForm({ altEmail: String(value || "").trim() })
            }
          />
          <ProfileFact
            icon="fas fa-phone"
            label="Landline"
            displayValue={form.landline}
            draftValue={form.landline}
            maxLength={15}
            onSave={(value) =>
              onPatchForm({ landline: String(value || "").trim() })
            }
          />
        </div>
      </div>

      <div className="eplanner-book__profile-group">
        <span className="eplanner-book__profile-group-label">Personal</span>
        <div className="eplanner-book__fact-grid">
          <ProfileFact
            icon="fas fa-birthday-cake"
            label="Date of birth"
            displayValue={form.dob ? formatDobDisplay(form.dob) : ""}
            draftValue={form.dob}
            inputType="date"
            onSave={(value) => onPatchForm({ dob: dobToInput(value) })}
          />
          <ProfileGenderFact
            value={form.gender}
            onChange={(next) =>
              onPatchForm({ gender: String(next || "").trim() })
            }
          />
        </div>
      </div>

      <div className="eplanner-book__profile-group">
        <span className="eplanner-book__profile-group-label">Next of kin</span>
        <div className="eplanner-book__fact-grid">
          <ProfileFact
            icon="fas fa-user-friends"
            label="Name"
            displayValue={form.kinName}
            draftValue={form.kinName}
            onSave={(value) =>
              onPatchForm({ kinName: String(value || "").trim() })
            }
          />
          <ProfileFact
            icon="fas fa-phone-alt"
            label="Contact"
            displayValue={form.kinContact}
            draftValue={form.kinContact}
            maxLength={11}
            onSave={(value) =>
              onPatchForm({ kinContact: String(value || "").trim() })
            }
          />
        </div>
      </div>
    </div>
  );
}

export default function AppointmentBookModal({
  open,
  onClose,
  userData,
  onSaved,
  mode = BOOK_MODES.BLANK,
  initialVolunteer = null,
  initialContext = null,
  /** Render the booking workspace as a full page (no modal chrome). */
  asPage = false,
}) {
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const {
    studyIds: filterStudyIds = [],
    groupId: filterGroupId = "",
    visitId: filterVisitId = "",
  } = useEplannerFilters();
  const isActive = Boolean(asPage || open);

  const [form, setForm] = useState(EMPTY_FORM);
  const [volunteerRow, setVolunteerRow] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [participantSearch, setParticipantSearch] = useState("");
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [studies, setStudies] = useState([]);
  const [groups, setGroups] = useState([]);
  const [visits, setVisits] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const orgStaffRef = useRef([]);
  const studyStaffIdsRef = useRef([]);
  const syncedWindowDateRef = useRef(false);
  const [defaultSlotMinutes, setDefaultSlotMinutes] = useState(0);
  const [orgDayStartTime, setOrgDayStartTime] = useState("");
  const [orgDayEndTime, setOrgDayEndTime] = useState("");
  const [orgCountryIso2, setOrgCountryIso2] = useState("GB");
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [confirmPrompt, setConfirmPrompt] = useState(null);
  const [windowRows, setWindowRows] = useState([]);
  const [bookedStudyRows, setBookedStudyRows] = useState([]);
  const [visitWindowMeta, setVisitWindowMeta] = useState([]);
  const [loadingWindows, setLoadingWindows] = useState(false);
  const [locationConflict, setLocationConflict] = useState("");

  const lockVolunteer =
    mode === BOOK_MODES.PREFILL_VOLUNTEER || mode === BOOK_MODES.CONTEXT;
  const lockStudyVisit = mode === BOOK_MODES.CONTEXT;
  /** From Participant Profile: keep details, hide the search/list column. */
  const hideParticipantSearch = mode === BOOK_MODES.PREFILL_VOLUNTEER;

  const occupancyBusyRanges = useMemo(
    () =>
      Array.isArray(initialContext?.busyRanges)
        ? initialContext.busyRanges
        : [],
    [initialContext?.busyRanges],
  );
  const occupancyFromTime = String(
    initialContext?.preferredFromTime || "",
  ).trim();

  // Same inputs legacy calendar icon uses: VolunteerId + StudyId
  const canShowWindows = Boolean(
    volunteerIdOf(volunteerRow) && form.studyId,
  );
  const canProjectWindows = Boolean(
    form.studyId && form.groupId && form.appointmentDate,
  );

  const studyOptions = useMemo(() => mapStudyOptions(studies), [studies]);
  const groupOptions = useMemo(
    () =>
      mapGroupOptions(
        groups,
        form.studyId ? [form.studyId] : [],
        studyOptions,
      ),
    [form.studyId, groups, studyOptions],
  );
  const visitOptions = useMemo(() => {
    const mapped = mapVisitOptions(
      visits,
      form.groupId ? [form.groupId] : [],
      { studyOptions, groupOptions },
    );
    return [
      ...mapped,
      { value: "ADHOCVISIT", label: "Adhoc visit", description: "" },
    ];
  }, [form.groupId, groupOptions, studyOptions, visits]);

  const isAdhoc = form.visitId === "ADHOCVISIT";
  const selectedVisitOption = useMemo(
    () => findOptionByValue(visitOptions, form.visitId),
    [form.visitId, visitOptions],
  );
  // Legacy: visit SlotDuration when set, else org CalendarDefaultSlotDuration, else 5.
  const timeIntervalMinutes = useMemo(() => {
    const visitSlot = Number(selectedVisitOption?.slotDuration);
    if (Number.isFinite(visitSlot) && visitSlot > 0) {
      return resolveTimeIntervalMinutes(visitSlot);
    }
    if (Number.isFinite(defaultSlotMinutes) && defaultSlotMinutes > 0) {
      return resolveTimeIntervalMinutes(defaultSlotMinutes);
    }
    return resolveTimeIntervalMinutes(5);
  }, [defaultSlotMinutes, selectedVisitOption?.slotDuration]);
  // Legacy: visit DayWindow* when set, else org CalenderDefaultStart/EndTime.
  const dayStartTime = useMemo(() => {
    const visitStart = String(selectedVisitOption?.dayStartTime || "").trim();
    return visitStart || orgDayStartTime || "";
  }, [orgDayStartTime, selectedVisitOption?.dayStartTime]);
  const dayEndTime = useMemo(() => {
    const visitEnd = String(selectedVisitOption?.dayEndTime || "").trim();
    return visitEnd || orgDayEndTime || "";
  }, [orgDayEndTime, selectedVisitOption?.dayEndTime]);
  // Legacy: Screening ID only when VisitNo === 0; Random ID whenever a study is selected.
  const isScreeningVisit = Boolean(
    !isAdhoc &&
      (selectedVisitOption?.isScreening ||
        selectedVisitOption?.visitNo === 0 ||
        /screening/i.test(String(selectedVisitOption?.label || ""))),
  );
  const showRandomisedId = Boolean(form.studyId);
  const showScreeningId = Boolean(form.studyId && isScreeningVisit);

  const smartWindowRows = useMemo(
    () =>
      buildSmartTentativeWindowRows({
        visitDetailRows: visitWindowMeta,
        plannerVisits: visits,
        bookedAppointments: bookedStudyRows,
        groupId: form.groupId,
        anchorDateIso: form.appointmentDate,
        selectedVisitId: form.visitId,
      }),
    [
      bookedStudyRows,
      form.appointmentDate,
      form.groupId,
      form.visitId,
      visitWindowMeta,
      visits,
    ],
  );

  const sortedWindowRows = useMemo(() => {
    if (!canProjectWindows) return [];
    return mergeApiAndSmartWindowRows({
      apiRows: windowRows,
      smartRows: smartWindowRows,
    });
  }, [canProjectWindows, smartWindowRows, windowRows]);

  const windowHint = useMemo(() => {
    if (!form.studyId) {
      return "Select a study to begin.";
    }
    if (!form.groupId) {
      return "Select a group to continue.";
    }
    if (!form.appointmentDate) {
      return "Select an appointment date to preview expected visit windows.";
    }
    if (loadingWindows && !smartWindowRows.length) return "Loading windows…";
    return "No visits found for this study and group.";
  }, [
    form.appointmentDate,
    form.groupId,
    form.studyId,
    loadingWindows,
    smartWindowRows.length,
  ]);

  // Location Occupancy: if selected location is busy at current time, auto-pick
  // the next free start using the active slot duration (visit → org → 5).
  useEffect(() => {
    if (!isActive) {
      setLocationConflict("");
      return;
    }
    if (
      !occupancyBusyRanges.length ||
      !form.locationId ||
      !form.appointmentDate ||
      !form.appointmentTime
    ) {
      setLocationConflict("");
      return;
    }

    const occupied = isSlotOccupied({
      appointmentDate: form.appointmentDate,
      time: form.appointmentTime,
      durationMinutes: timeIntervalMinutes,
      busyRanges: occupancyBusyRanges,
    });

    if (!occupied) {
      setLocationConflict("");
      return;
    }

    const free = nextFreeSlotTime({
      appointmentDate: form.appointmentDate,
      fromTime: occupancyFromTime || form.appointmentTime,
      intervalMinutes: timeIntervalMinutes,
      dayStartTime,
      dayEndTime,
      busyRanges: occupancyBusyRanges,
    });

    if (free && free !== form.appointmentTime) {
      setForm((current) =>
        current.appointmentTime === free
          ? current
          : { ...current, appointmentTime: free },
      );
      setLocationConflict("");
      return;
    }

    setLocationConflict(
      "This location is already occupied at the selected time.",
    );
  }, [
    dayEndTime,
    dayStartTime,
    form.appointmentDate,
    form.appointmentTime,
    form.locationId,
    isActive,
    occupancyBusyRanges,
    occupancyFromTime,
    timeIntervalMinutes,
  ]);

  // Single-group studies: auto-select the (non-screening) group.
  // Multi-group: leave choice to the user.
  useEffect(() => {
    if (!isActive || lockStudyVisit || !form.studyId) return;
    if (form.groupId) return;
    if (!groupOptions.length) return;

    if (groupOptions.length === 1) {
      setForm((current) =>
        current.groupId ? current : { ...current, groupId: groupOptions[0].value },
      );
      return;
    }

    const nonScreening = groupOptions.filter(
      (option) => !/screening/i.test(String(option?.label || "")),
    );
    if (nonScreening.length === 1) {
      setForm((current) =>
        current.groupId
          ? current
          : { ...current, groupId: nonScreening[0].value },
      );
    }
  }, [
    form.groupId,
    form.studyId,
    groupOptions,
    lockStudyVisit,
    isActive,
  ]);

  const filteredParticipants = useMemo(() => {
    const needle = participantSearch.trim().toLowerCase();
    const list = Array.isArray(participants) ? participants : [];
    if (!needle) return list;
    return list.filter((row) => {
      const haystack = [
        getVolunteerDisplayName(row),
        pickVolunteerField(row, ["VolunteerNo", "VolId"]),
        pickVolunteerField(row, ["ContactNumber", "MobileNo"]),
        pickVolunteerField(row, ["EmailId"]),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [participantSearch, participants]);

  const setField = useCallback((key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  }, []);

  const patchForm = useCallback((patch) => {
    setForm((current) => ({ ...current, ...patch }));
  }, []);

  const reset = useCallback(() => {
    if (lockVolunteer) {
      setConfirmPrompt(null);
      if (!lockStudyVisit) {
        setWindowRows([]);
        setBookedStudyRows([]);
        setVisitWindowMeta([]);
      }
      setForm((current) => {
        const clearedDate = lockStudyVisit
          ? current.appointmentDate
          : todayIsoDate();
        const clearedTime = lockStudyVisit
          ? current.appointmentTime
          : getNextAvailableTime({
              appointmentDate: clearedDate,
              intervalMinutes: timeIntervalMinutes,
              dayStartTime,
              dayEndTime,
            });
        return {
          ...current,
          studyId: lockStudyVisit ? current.studyId : "",
          groupId: lockStudyVisit ? current.groupId : "",
          visitId: lockStudyVisit ? current.visitId : "",
          adhocReason: "",
          appointmentType: "Clinic",
          appointmentDate: clearedDate,
          appointmentTime: clearedTime,
          ...EMPTY_LOCATION,
          reason: "",
          staffIds: lockStudyVisit ? [...studyStaffIdsRef.current] : [],
        };
      });
      return;
    }
    const today = todayIsoDate();
    setForm({
      ...EMPTY_FORM,
      appointmentDate: today,
      appointmentTime: getNextAvailableTime({
        appointmentDate: today,
        intervalMinutes: timeIntervalMinutes,
        dayStartTime,
        dayEndTime,
      }),
    });
    setVolunteerRow(null);
    setConfirmPrompt(null);
    setParticipantSearch("");
    setWindowRows([]);
    setBookedStudyRows([]);
    setVisitWindowMeta([]);
  }, [
    dayEndTime,
    dayStartTime,
    lockStudyVisit,
    lockVolunteer,
    timeIntervalMinutes,
  ]);

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setVolunteerRow(null);
    setConfirmPrompt(null);
    setParticipantSearch("");
    setWindowRows([]);
    setBookedStudyRows([]);
    setVisitWindowMeta([]);
    setLocationPickerOpen(false);
    setLocationConflict("");
    onClose?.();
  };

  useEffect(() => {
    if (!isActive || !userData?.OrganizationId) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingLookups(true);
      setLoadingParticipants(true);
      try {
        const [bundle, staffRows, volunteerRows, defaultSetup, setupResponse] =
          await Promise.all([
            fetchPlannerStudyBundle(userData),
            getInternalActiveStaffLookup(userData, {}).catch(() => []),
            fetchActiveVolunteers(userData).catch(() => []),
            fetchPlannerDefaultSetup(userData).catch(() => ({
              slotMinutes: 0,
              startTime: "",
              endTime: "",
            })),
            fetchOrganizationSetup(
              userData.OrganizationId,
              userData.StaffId,
            ).catch(() => null),
          ]);
        if (cancelled) return;
        setStudies(bundle?.studies || []);
        const orgStaff = Array.isArray(staffRows) ? staffRows : [];
        orgStaffRef.current = orgStaff;
        setStaffOptions(orgStaff);
        setParticipants(Array.isArray(volunteerRows) ? volunteerRows : []);
        setDefaultSlotMinutes(Number(defaultSetup?.slotMinutes) || 0);
        setOrgDayStartTime(String(defaultSetup?.startTime || "").trim());
        setOrgDayEndTime(String(defaultSetup?.endTime || "").trim());
        const setupRow = setupResponse
          ? parseOrganizationSetupResponse(setupResponse)?.setupRow
          : null;
        setOrgCountryIso2(
          String(setupRow?.CountryIso2 || "GB")
            .trim()
            .toUpperCase() || "GB",
        );
      } catch {
        if (!cancelled) {
          setStudies([]);
          orgStaffRef.current = [];
          setStaffOptions([]);
          setParticipants([]);
          setDefaultSlotMinutes(0);
          setOrgDayStartTime("");
          setOrgDayEndTime("");
          setOrgCountryIso2("GB");
        }
      } finally {
        if (!cancelled) {
          setLoadingLookups(false);
          setLoadingParticipants(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isActive, userData]);

  useEffect(() => {
    if (!isActive) {
      syncedWindowDateRef.current = false;
      return;
    }
    const fromVolunteer = applyVolunteerToForm(
      initialVolunteer,
      orgCountryIso2,
    );
    const contextStudy =
      initialContext?.studyId ||
      fromVolunteer.studyId ||
      filterStudyIds[0] ||
      "";
    const defaultDate =
      toBookingDateIso(initialContext?.date) || todayIsoDate();
    const contextLocation = initialContext?.locationId
      ? {
          locationId: String(initialContext.locationId || "").trim(),
          buildingId: String(initialContext.buildingId || "").trim(),
          floorId: String(initialContext.floorId || "").trim(),
          roomId: String(initialContext.roomId || "").trim(),
          roomTypeId: String(
            initialContext.roomTypeId || initialContext.capacityId || "",
          ).trim(),
          locationType: String(initialContext.locationType || "").trim(),
          locationLabel: String(initialContext.locationLabel || "").trim(),
        }
      : EMPTY_LOCATION;
    const next = {
      ...EMPTY_FORM,
      ...fromVolunteer,
      studyId: contextStudy,
      groupId: initialContext?.groupId || filterGroupId || "",
      visitId: initialContext?.visitId || filterVisitId || "",
      appointmentDate: defaultDate,
      appointmentTime:
        initialContext?.time ||
        getNextAvailableTime({
          appointmentDate: defaultDate,
          intervalMinutes: timeIntervalMinutes,
          dayStartTime,
          dayEndTime,
        }),
      ...contextLocation,
      appointmentType: initialContext?.appointmentType || "Clinic",
    };
    setForm(next);
    setVolunteerRow(initialVolunteer || null);
    setConfirmPrompt(null);
    setParticipantSearch("");
    setLocationPickerOpen(false);
    syncedWindowDateRef.current = false;
  }, [
    filterGroupId,
    filterStudyIds,
    filterVisitId,
    initialContext,
    initialVolunteer,
    isActive,
  ]);

  useEffect(() => {
    if (!isActive || !orgCountryIso2) return;
    setForm((current) => {
      const volunteerIso = String(
        volunteerRow?.MobileCountryIso2 ||
          volunteerRow?.mobileCountryIso2 ||
          "",
      )
        .trim()
        .toUpperCase();
      const nextIso = volunteerIso || orgCountryIso2;
      const nextMobile =
        sanitizeNationalMobileInput(current.mobile, nextIso) ||
        String(current.mobile || "").replace(/\D/g, "") ||
        current.mobile;
      if (
        current.mobileCountryIso2 === nextIso &&
        current.mobile === nextMobile
      ) {
        return current;
      }
      return {
        ...current,
        mobileCountryIso2: nextIso,
        mobile: nextMobile,
      };
    });
  }, [isActive, orgCountryIso2, volunteerRow]);

  // Window → Book: once the timeline loads, align date with that visit's Expected start.
  useEffect(() => {
    if (!isActive || !lockStudyVisit || syncedWindowDateRef.current) return;
    const visitKey = String(form.visitId || "").trim().toLowerCase();
    if (!visitKey || !sortedWindowRows.length) return;
    const selected = sortedWindowRows.find((row) => {
      const id = String(
        row?.VisitsPerStudyId ??
          row?.visitsPerStudyId ??
          row?.VisitIdRef ??
          row?.visitIdRef ??
          "",
      )
        .trim()
        .toLowerCase();
      return id && id === visitKey;
    });
    const expectedIso = getWindowVisitExpectedDateIso(selected);
    if (!expectedIso) return;
    syncedWindowDateRef.current = true;
    setForm((current) => {
      if (current.appointmentDate === expectedIso) return current;
      const nextTime = getNextAvailableTime({
        appointmentDate: expectedIso,
        intervalMinutes: timeIntervalMinutes,
        dayStartTime,
        dayEndTime,
      });
      const keepTime =
        current.appointmentTime &&
        !isPastSlot(current.appointmentTime, expectedIso);
      return {
        ...current,
        appointmentDate: expectedIso,
        appointmentTime: keepTime ? current.appointmentTime : nextTime,
      };
    });
  }, [
    dayEndTime,
    dayStartTime,
    form.visitId,
    isActive,
    lockStudyVisit,
    sortedWindowRows,
    timeIntervalMinutes,
  ]);

  // When org/visit hours load: ensure today default + next available if empty/past.
  useEffect(() => {
    if (!isActive) return;
    // Keep calendar / context prefill (date + time) instead of next-available override.
    if (initialContext?.date && initialContext?.time) return;
    setForm((current) => {
      const date = current.appointmentDate || todayIsoDate();
      const nextTime = getNextAvailableTime({
        appointmentDate: date,
        intervalMinutes: timeIntervalMinutes,
        dayStartTime,
        dayEndTime,
      });
      const timeNeedsUpdate =
        !current.appointmentTime ||
        isPastSlot(current.appointmentTime, date);
      if (date === current.appointmentDate && !timeNeedsUpdate) {
        return current;
      }
      return {
        ...current,
        appointmentDate: date,
        appointmentTime: timeNeedsUpdate ? nextTime : current.appointmentTime,
      };
    });
  }, [
    dayEndTime,
    dayStartTime,
    initialContext?.date,
    initialContext?.time,
    isActive,
    timeIntervalMinutes,
  ]);

  useEffect(() => {
    if (!isActive || !initialVolunteer || !participants.length) return;
    const match = participants.find((row) => sameVolunteer(row, initialVolunteer));
    if (match) setVolunteerRow(match);
  }, [initialVolunteer, isActive, participants]);

  useEffect(() => {
    if (!isActive || !userData?.OrganizationId || !form.studyId) {
      setGroups([]);
      setVisits([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchPlannerGroupsAndVisits(userData, [
          form.studyId,
        ]);
        if (cancelled) return;
        setGroups(next?.groups || []);
        setVisits(next?.visits || []);
      } catch {
        if (!cancelled) {
          setGroups([]);
          setVisits([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.studyId, isActive, userData]);

  // Legacy: study StaffList only, all selected by default; PI flagged for UI.
  useEffect(() => {
    if (!isActive || !userData?.OrganizationId || !form.studyId) {
      studyStaffIdsRef.current = [];
      setStaffOptions([]);
      setForm((current) =>
        current.staffIds?.length ? { ...current, staffIds: [] } : current,
      );
      return undefined;
    }
    const studyRow = (studies || []).find((row) => {
      const scheduleId = String(
        row?.StudyScheduleId ?? row?.studyScheduleId ?? "",
      ).trim();
      const studyId = String(row?.StudyId ?? row?.studyId ?? "").trim();
      const selected = String(form.studyId).trim();
      return (
        scheduleId.toLowerCase() === selected.toLowerCase() ||
        studyId.toLowerCase() === selected.toLowerCase()
      );
    });
    const piStaffId = String(
      studyRow?.PrincipalInvestigator ??
        studyRow?.principalInvestigator ??
        studyRow?.PrincipalInvestigatorId ??
        studyRow?.principalInvestigatorId ??
        "",
    ).trim();
    const piFromOrg = orgStaffRef.current.find(
      (row) =>
        String(row?.StaffId ?? row?.staffId ?? "")
          .trim()
          .toLowerCase() === piStaffId.toLowerCase(),
    );
    const piStaffName = String(
      piFromOrg?.StaffName ??
        piFromOrg?.staffName ??
        [piFromOrg?.FirstName, piFromOrg?.LastName].filter(Boolean).join(" ") ??
        "",
    ).trim();

    let cancelled = false;
    (async () => {
      try {
        const studyStaff = await fetchStudyAssignedStaff({
          userData,
          studyId: form.studyId,
          piStaffId,
          piStaffName,
        });
        if (cancelled) return;
        const options = Array.isArray(studyStaff) ? studyStaff : [];
        setStaffOptions(options);
        const ids = options.map((row) => String(row.StaffId));
        studyStaffIdsRef.current = ids;
        setForm((current) => ({ ...current, staffIds: ids }));
      } catch {
        if (!cancelled) {
          studyStaffIdsRef.current = [];
          setStaffOptions([]);
          setForm((current) => ({ ...current, staffIds: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.studyId, isActive, studies, userData]);

  // Visit schedule meta for smart next-visit date projection (study + group + date).
  useEffect(() => {
    if (!isActive || !userData?.OrganizationId || !form.studyId) {
      setVisitWindowMeta([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchStudyVisitWindowMeta({
          userData,
          studyId: form.studyId,
        });
        if (!cancelled) setVisitWindowMeta(Array.isArray(rows) ? rows : []);
      } catch (error) {
        if (!cancelled) {
          setVisitWindowMeta([]);
          console.error(error);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.studyId, isActive, userData]);

  // Booked visits (actual dates) + legacy window rows for this volunteer/study.
  useEffect(() => {
    if (!isActive || !canShowWindows) {
      setWindowRows([]);
      setBookedStudyRows([]);
      setLoadingWindows(false);
      return undefined;
    }
    const volunteerId = volunteerIdOf(volunteerRow);
    let cancelled = false;
    (async () => {
      setLoadingWindows(true);
      try {
        const [windows, booked] = await Promise.all([
          fetchWindowAppointments({
            userData,
            volunteerId,
            studyId: form.studyId,
          }).catch(() => []),
          fetchVolunteerStudyAppointments({
            userData,
            volunteerId,
            studyId: form.studyId,
          }).catch(() => []),
        ]);
        if (cancelled) return;
        setWindowRows(Array.isArray(windows) ? windows : []);
        setBookedStudyRows(Array.isArray(booked) ? booked : []);
      } catch (error) {
        if (!cancelled) {
          setWindowRows([]);
          setBookedStudyRows([]);
          console.error(error);
        }
      } finally {
        if (!cancelled) setLoadingWindows(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canShowWindows, form.studyId, isActive, userData, volunteerRow]);

  const applyVolunteerSelection = (row) => {
    if (lockVolunteer) return;
    const patch = applyVolunteerToForm(row, orgCountryIso2);
    setVolunteerRow(row);
    setForm((current) => ({
      ...current,
      ...patch,
      studyId: current.studyId || patch.studyId || "",
    }));
  };

  const buildPayload = (overrides = {}) => {
    const startTime = toLegacyStartTime(
      form.appointmentDate,
      form.appointmentTime,
    );
    const volId = guidOrNull(volunteerIdOf(volunteerRow));
    const locationId = guidOrNull(form.locationId);
    const rawOverrides =
      overrides && typeof overrides === "object" ? { ...overrides } : {};
    // Keep GUID overrides null-safe (e.g. empty LocationId from callers).
    [
      "LocationId",
      "LocationIdRef",
      "BuildingId",
      "FloorId",
      "RoomId",
      "RoomTypeId",
      "VisitId",
      "AppointmentId",
      "NoteId",
    ].forEach((key) => {
      if (key in rawOverrides) {
        rawOverrides[key] = guidOrNull(rawOverrides[key]);
      }
    });
    return {
      OrganizationId: guidOrNull(userData?.OrganizationId),
      CreatedBy: guidOrNull(userData?.StaffId),
      StaffIdRef: guidOrNull(userData?.StaffId),
      VolunteerId: volId,
      VolIdRef: volId,
      VolunteerName: form.volunteerName.trim(),
      VolMobile: sanitizeNationalMobileInput(
        form.mobile,
        form.mobileCountryIso2 || orgCountryIso2,
      ),
      MobileCountryIso2: resolveMobileCountryIso2(form, orgCountryIso2),
      VolLandLineNo: form.landline.trim() || null,
      PEmailId: form.altEmail.trim() || null,
      WEmailId: form.email.trim(),
      DOB: dobToApi(form.dob),
      Gender: form.gender || null,
      KinName: form.kinName.trim() || null,
      KinContactNo: form.kinContact.trim() || null,
      Base64Image: String(form.photoBase64 || "").startsWith("data:")
        ? form.photoBase64
        : null,
      ProfilePictureUrl: String(form.photoBase64 || "").startsWith("data:")
        ? null
        : form.photoUrl || null,
      StudyId: guidOrNull(form.studyId),
      VisitId: isAdhoc ? null : guidOrNull(form.visitId),
      GroupNameIdRef: guidOrNull(form.groupId),
      StartTime: startTime,
      EndTime: null,
      AppointmentId: null,
        AppointmentStatus: "Confirmed",
        ActionType: "BookAppointment",
      AppointmentType: form.appointmentType,
      AdhocVisit: isAdhoc ? 1 : 0,
      AdhocReason: isAdhoc ? form.adhocReason.trim() : null,
      RandomisedId: showRandomisedId ? form.randomisedId.trim() || null : null,
      // Legacy: Screening ID only for Visit 0; other visits send empty ScreeningId.
      ScreeningId: showScreeningId ? form.screeningId.trim() || null : null,
      VerifyRandomisedId: Boolean(
        showRandomisedId &&
          form.randomisedId.trim() &&
          form.verifyRandomisedId,
      ),
      VerifyScreeningId: Boolean(
        showScreeningId && form.screeningId.trim() && form.verifyScreeningId,
      ),
      // Legacy first book sends empty reason; filled only on out-of-window continue.
      ReasonForBookApp: null,
        OutOfRangeApp: 0,
        AllDay: 0,
      // EPlan_Appointment.*Id columns are uniqueidentifier — "" throws conversion error.
      LocationId: locationId,
      LocationIdRef: locationId,
      BuildingId: guidOrNull(form.buildingId),
      FloorId: guidOrNull(form.floorId),
      RoomId: guidOrNull(form.roomId),
      RoomTypeId: guidOrNull(form.roomTypeId),
      Type: form.locationType || null,
      NoteId: null,
      IsLinkedVolunteer: true,
      Staffs: (Array.isArray(form.staffIds) ? form.staffIds : [])
        .map((id) => guidOrNull(id))
        .filter(Boolean),
      ...rawOverrides,
    };
  };

  const validate = () => {
    if (!volunteerRow || !form.volunteerName.trim()) {
      return "Select an active participant.";
    }
    const phoneResult = validateNationalMobile(form.mobile, {
      countryIso2: form.mobileCountryIso2 || orgCountryIso2,
      required: true,
    });
    if (!phoneResult.valid) {
      return (
        phoneResult.message || "Selected participant has no mobile number."
      );
    }
    if (!form.dob) return "Selected participant has no date of birth.";
    if (!form.email.trim()) return "Selected participant has no email.";
    if (!form.studyId) return "Select a study.";
    if (!form.groupId) return "Select a group.";
    if (!form.visitId) return "Select a visit.";
    if (isAdhoc && !form.adhocReason.trim()) {
      return "Adhoc reason is required.";
    }
    if (!form.appointmentType) return "Select appointment type.";
    if (!form.appointmentDate || !form.appointmentTime) {
      return "Appointment date and time are required.";
    }
    const start = new Date(`${form.appointmentDate}T${form.appointmentTime}`);
    if (Number.isNaN(start.getTime()) || start.getTime() < Date.now()) {
      return "Please select a future date/time.";
    }
    if (locationConflict) return locationConflict;
    if (
      occupancyBusyRanges.length &&
      form.locationId &&
      isSlotOccupied({
        appointmentDate: form.appointmentDate,
        time: form.appointmentTime,
        durationMinutes: timeIntervalMinutes,
        busyRanges: occupancyBusyRanges,
      })
    ) {
      return "This location is already occupied at the selected time.";
    }
    return "";
  };

  const bookingBlockReason = confirmPrompt
    ? "Resolve the confirmation prompt before booking."
    : validate();
  const canBook = !bookingBlockReason;

  const submitBooking = async (overrides = {}) => {
    const error = validate();
    if (error) {
      showAlert({ message: error, type: "warning" });
      return;
    }

    showLoader("Booking appointment…");
    try {
      const response = await bookAppointmentRequest(buildPayload(overrides));
      const result = parseAppointmentActivityResult(response);

      if (result.success === 1) {
        showAlert({ message: result.message, type: "success" });
        setConfirmPrompt(null);
      onSaved?.();
        if (asPage) {
          reset();
          setLocationPickerOpen(false);
        } else {
      handleClose();
        }
        return;
      }

      if (result.success === 2) {
        setConfirmPrompt({
          kind: "reason",
          message: result.message,
          outOfRangeApp: 1,
        });
        return;
      }

      if (result.success === 3) {
        setConfirmPrompt({
          kind: "confirm",
          message: result.message,
          outOfRangeApp: 2,
        });
        return;
      }

      showAlert({
        message: result.message || "Could not book appointment.",
        type: "danger",
      });
    } catch (error) {
      showAlert({
        message: appointmentNetworkErrorMessage(error, "Could not book appointment."),
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handleConfirmContinue = async () => {
    if (!confirmPrompt) return;
    if (confirmPrompt.kind === "reason" && !form.reason.trim()) {
      showAlert({
        message: "Enter a reason to continue booking.",
        type: "warning",
      });
      return;
    }
    await submitBooking({
      OutOfRangeApp: confirmPrompt.outOfRangeApp,
      ReasonForBookApp: form.reason.trim(),
    });
  };

  if (!asPage && !open) {
  return (
    <>
        <InfoAlert {...alertState} onClose={hideAlert} />
      </>
    );
  }

  const workspace = (
        <div className="eplanner-book eplanner-book--workspace">
          <div
            className={`eplanner-book__columns${
              hideParticipantSearch ? " eplanner-book__columns--no-search" : ""
            }`}
          >
            {hideParticipantSearch ? null : (
            <aside className="eplanner-book__col eplanner-book__col--participants">
              <div className="eplanner-book__col-head">
                <span className="eplanner-book__col-title">Active participants</span>
                {loadingParticipants ? (
                  <span className="eplanner-book__badge">Loading…</span>
                ) : (
                  <span className="eplanner-book__badge">
                    {filteredParticipants.length}
                  </span>
                )}
              </div>
              <div className="eplanner-book__col-toolbar">
                <input
                  type="search"
                  className="form-control form-control-sm"
                  placeholder="Search name, ID, mobile, email…"
                  value={participantSearch}
                  disabled={lockVolunteer}
                  onChange={(event) => setParticipantSearch(event.target.value)}
            />
          </div>
              <div className="eplanner-book__participant-list" role="listbox">
                {!loadingParticipants && filteredParticipants.length === 0 ? (
                  <p className="eplanner-book__empty">No active participants found.</p>
                ) : null}
                {filteredParticipants.map((row, index) => {
                  const selected = sameVolunteer(row, volunteerRow);
                  const name = getVolunteerDisplayName(row);
                  const volNo =
                    pickVolunteerField(row, ["VolunteerNo", "VolId"]) || "—";
                  const mobile =
                    formatMobileDisplay(
                      pickVolunteerField(row, ["ContactNumber", "MobileNo"]),
                      resolveMobileCountryIso2(row, orgCountryIso2),
                    ) || "—";
                  return (
                    <button
                      key={volunteerIdOf(row) || `${volNo}-${index}`}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`eplanner-book__participant-item${
                        selected ? " is-selected" : ""
                      }`}
                      disabled={lockVolunteer && !selected}
                      onClick={() => applyVolunteerSelection(row)}
                    >
                      <strong>{name}</strong>
                      <span>
                        {volNo} · {mobile}
                      </span>
                    </button>
                  );
                })}
              </div>
            </aside>
            )}

            <section className="eplanner-book__col eplanner-book__col--info">
              <div className="eplanner-book__col-head">
                <span className="eplanner-book__col-title">Participant details</span>
              </div>
              {volunteerRow ? (
                <ParticipantDetailsPanel
                  form={form}
                  onPatchForm={patchForm}
                  orgCountryIso2={orgCountryIso2}
                />
              ) : (
                <p className="eplanner-book__empty">
                  {hideParticipantSearch
                    ? "Loading participant details…"
                    : "Select a participant from the list to view details."}
                </p>
              )}
            </section>

            <section className="eplanner-book__col eplanner-book__col--booking">
              <div className="eplanner-book__col-head">
                <span className="eplanner-book__col-title">Appointment booking</span>
                {loadingLookups ? (
                  <span className="eplanner-book__badge">Loading…</span>
                ) : null}
              </div>

              <div className="eplanner-book__booking-scroll">
                <div className="eplanner-book__grid eplanner-book__grid--booking">
                  <Field label="Study" required>
                    {lockStudyVisit ? (
                      <input
                        type="text"
                        className="form-control"
                        disabled
                        value={
                          findOptionByValue(studyOptions, form.studyId)
                            ?.label ||
                          initialContext?.studyName ||
                          form.studyId
                        }
                      />
                    ) : (
            <Dropdown
                        options={studyOptions}
              selected={
                          findOptionByValue(studyOptions, form.studyId) || null
                        }
                        onSelect={(option) => {
                          setForm((current) => ({
                            ...current,
                            studyId: option?.value || "",
                            groupId: "",
                            visitId: "",
                            screeningId: "",
                            randomisedId: "",
                            staffIds: [],
                          }));
                        }}
                      />
                    )}
                  </Field>
                  <Field label="Group" required>
                    {lockStudyVisit ? (
            <input
                        type="text"
              className="form-control"
                        disabled
                        value={
                          findOptionByValue(groupOptions, form.groupId)
                            ?.label ||
                          initialContext?.groupName ||
                          form.groupId
                        }
                      />
                    ) : (
                      <Dropdown
                        options={groupOptions}
                        selected={
                          findOptionByValue(groupOptions, form.groupId) || null
                        }
                        onSelect={(option) => {
                          setForm((current) => ({
                            ...current,
                            groupId: option?.value || "",
                            visitId: "",
                            screeningId: "",
                          }));
                        }}
                      />
                    )}
                  </Field>
                  <Field label="Visit" required>
                    {lockStudyVisit ? (
            <input
                        type="text"
              className="form-control"
                        disabled
                        value={
                          findOptionByValue(visitOptions, form.visitId)
                            ?.label ||
                          initialContext?.visitName ||
                          form.visitId
                        }
                      />
                    ) : (
                      <Dropdown
                        options={visitOptions}
                        selected={
                          findOptionByValue(visitOptions, form.visitId) || null
                        }
                        onSelect={(option) => {
                          const nextVisitId = option?.value || "";
                          const nextIsScreening =
                            option?.isScreening ||
                            option?.visitNo === 0 ||
                            /screening/i.test(String(option?.label || ""));
                          setForm((current) => ({
                            ...current,
                            visitId: nextVisitId,
                            screeningId: nextIsScreening
                              ? current.screeningId
                              : "",
                          }));
                        }}
                      />
                    )}
                  </Field>
                  <div className="eplanner-book__field eplanner-book__field--datetime">
                    <span className="eplanner-book__label">
                      Appointment date / time
                      <em aria-hidden="true">*</em>
                    </span>
                    <div className="eplanner-book__datetime-pair">
                      <DatePicker
                        selected={parseIsoDateOnly(form.appointmentDate)}
                        onChange={(date) => {
                          const nextDate = date
                            ? formatIsoDateOnly(date)
                            : todayIsoDate();
                          const nextTime = getNextAvailableTime({
                            appointmentDate: nextDate,
                            intervalMinutes: timeIntervalMinutes,
                            dayStartTime,
                            dayEndTime,
                          });
                          setForm((current) => {
                            const keepTime =
                              current.appointmentTime &&
                              !isPastSlot(current.appointmentTime, nextDate);
                            return {
                              ...current,
                              appointmentDate: nextDate,
                              appointmentTime: keepTime
                                ? current.appointmentTime
                                : nextTime,
                            };
                          });
                        }}
                        className="form-control eplanner-book__datepicker-input"
                        wrapperClassName="eplanner-book__datepicker"
                        calendarClassName="eplanner-book__datepicker-calendar"
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select date"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        todayButton="Today"
                        minDate={new Date()}
                        popperProps={{ strategy: "fixed" }}
                        popperPlacement="bottom-start"
                        aria-label="Appointment date"
                      />
                      <AppointmentTimePicker
                        value={form.appointmentTime}
                        appointmentDate={form.appointmentDate || todayIsoDate()}
                        intervalMinutes={timeIntervalMinutes}
                        dayStartTime={dayStartTime}
                        dayEndTime={dayEndTime}
                        onChange={(next) => {
                          setForm((current) => ({
                            ...current,
                            appointmentTime: next,
                          }));
                        }}
            />
          </div>
                  </div>
                  <Field
                    label="Appointment type"
                    required
                    className="eplanner-book__field--appt-type"
                  >
                    <div className="gender-selector gender-selector-md eplanner-book__appt-type">
                      {APPOINTMENT_TYPE_OPTIONS.map((option) => {
                        const selected =
                          String(form.appointmentType || "") === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            className={`gender-btn gender-btn-md${
                              selected ? " selected" : ""
                            }`}
                            onClick={() =>
                              setField("appointmentType", option.value)
                            }
                            aria-pressed={selected}
                            title={option.label}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                  <Field label="Location" error={locationConflict}>
                    <div
                      className={[
                        "eplanner-book__location-field",
                        !form.appointmentDate || !form.appointmentTime
                          ? "is-disabled"
                          : "",
                        locationConflict ? "is-invalid" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <button
                        type="button"
                        className={[
                          "form-control form-control-sm eplanner-book__location-trigger",
                          form.locationLabel ? "" : "is-placeholder",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        disabled={!form.appointmentDate || !form.appointmentTime}
                        title={
                          !form.appointmentDate || !form.appointmentTime
                            ? "Select date and time first"
                            : form.locationLabel || "Choose location"
                        }
                        onClick={() => setLocationPickerOpen(true)}
                      >
                        <span>
                          {form.locationLabel ||
                            (!form.appointmentDate || !form.appointmentTime
                              ? "Select date & time first"
                              : "Choose location")}
                        </span>
                      </button>
                      {form.locationLabel ? (
                        <button
                          type="button"
                          className="eplanner-book__location-clear"
                          title="Clear location"
                          aria-label="Clear location"
                          disabled={
                            !form.appointmentDate || !form.appointmentTime
                          }
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setForm((current) => ({
                              ...current,
                              locationId: "",
                              buildingId: "",
                              floorId: "",
                              roomId: "",
                              roomTypeId: "",
                              locationType: "",
                              locationLabel: "",
                            }));
                          }}
                        >
                          <i className="fas fa-times" aria-hidden="true" />
                        </button>
                      ) : (
                        <span
                          className="eplanner-book__location-clear is-placeholder"
                          aria-hidden="true"
                        >
                          <i className="fas fa-map-marker-alt" />
                        </span>
                      )}
                    </div>
                  </Field>
                  {showRandomisedId ? (
                    <Field
                      label="Randomisation ID"
                      className="eplanner-book__field--compact"
                    >
                      <div className="eplanner-book__id-stack">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={form.randomisedId}
                          onChange={(event) => {
                            const next = event.target.value;
                            setForm((current) => ({
                              ...current,
                              randomisedId: next,
                              verifyRandomisedId: false,
                            }));
                          }}
                          placeholder="N/A"
                        />
                        {form.randomisedId.trim() ? (
                          <label className="eplanner-book__id-verify">
                            <input
                              type="checkbox"
                              checked={Boolean(form.verifyRandomisedId)}
                              onChange={(event) =>
                                setField(
                                  "verifyRandomisedId",
                                  event.target.checked,
                                )
                              }
                            />
                            <span>Verified</span>
                          </label>
                        ) : null}
                      </div>
                    </Field>
                  ) : null}
                  {showScreeningId ? (
                    <Field
                      label="Screening ID"
                      className="eplanner-book__field--compact"
                    >
                      <div className="eplanner-book__id-stack">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={form.screeningId}
                          onChange={(event) => {
                            const next = event.target.value;
                            setForm((current) => ({
                              ...current,
                              screeningId: next,
                              verifyScreeningId: false,
                            }));
                          }}
                          placeholder="N/A"
                        />
                        {form.screeningId.trim() ? (
                          <label className="eplanner-book__id-verify">
                            <input
                              type="checkbox"
                              checked={Boolean(form.verifyScreeningId)}
                              onChange={(event) =>
                                setField(
                                  "verifyScreeningId",
                                  event.target.checked,
                                )
                              }
                            />
                            <span>Verified</span>
                          </label>
                        ) : null}
                      </div>
                    </Field>
                  ) : null}
                  {isAdhoc ? (
                    <Field
                      label="Adhoc reason"
                      required
                      className="eplanner-book__span-2"
                    >
                      <textarea
              className="form-control"
                        rows={2}
                        value={form.adhocReason}
                        onChange={(event) =>
                          setField("adhocReason", event.target.value)
                        }
                      />
                    </Field>
                  ) : null}
                  <div className="eplanner-book__field eplanner-book__field--assigned-staff">
                    <span className="eplanner-book__label">Assigned staff</span>
                    <MilestoneRecipientStaff
                      selectedStaffIds={form.staffIds}
                      allStaff={staffOptions}
                      disabled={false}
                      emptyStaffMessage="No study staff available."
                      pickerShowsAll
                      pickerSubtitle="Study staff only"
                      onChange={(ids) => setField("staffIds", ids)}
            />
          </div>
        </div>

                {confirmPrompt ? (
                  <div className="eplanner-book__confirm">
                    <strong>
                      {confirmPrompt.kind === "reason"
                        ? "Reason required"
                        : "Confirm booking"}
                    </strong>
                    <p>{confirmPrompt.message}</p>
                    {confirmPrompt.kind === "reason" ? (
                      <input
                        type="text"
                        className="form-control mb-2"
                        value={form.reason}
                        onChange={(event) =>
                          setField("reason", event.target.value)
                        }
                        placeholder="Enter reason"
                      />
                    ) : null}
                    <div className="eplanner-book__confirm-actions">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setConfirmPrompt(null)}
                      >
            Cancel
          </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={
                          confirmPrompt.kind === "reason" &&
                          !String(form.reason || "").trim()
                        }
                        title={
                          confirmPrompt.kind === "reason" &&
                          !String(form.reason || "").trim()
                            ? "Enter a reason to continue"
                            : undefined
                        }
                        onClick={handleConfirmContinue}
                      >
                        Continue booking
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="eplanner-book__footer">
                <div className="eplanner-book__footer-right">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={reset}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={!canBook}
                    title={bookingBlockReason || "Book appointment"}
                    onClick={() => submitBooking()}
                  >
            Book appointment
          </button>
        </div>
              </div>
            </section>

            <aside className="eplanner-book__col eplanner-book__col--windows">
              <div className="eplanner-book__col-head eplanner-book__col-head--windows">
                <div className="eplanner-book__window-head-text">
                  <span className="eplanner-book__col-title">Window appointments</span>
                  {canProjectWindows ? (
                    <p className="eplanner-book__window-caption">
                      Expected windows for this study and group
                    </p>
                  ) : null}
                </div>
                {sortedWindowRows.length ? (
                  <span className="eplanner-book__badge eplanner-book__badge--windows">
                    {sortedWindowRows.length}
                  </span>
                ) : null}
              </div>
              <div className="eplanner-book__window-list">
                {sortedWindowRows.length ? (
                  <ol className="eplanner-book__window-timeline">
                    {sortedWindowRows.map((row, index) => {
                      const meta = formatWindowAppointmentRow(row);
                      const visitId = String(
                        row?.VisitsPerStudyId ??
                          row?.visitsPerStudyId ??
                          row?.VisitIdRef ??
                          row?.visitIdRef ??
                          "",
                      ).trim();
                      const selected =
                        visitId &&
                        String(form.visitId || "").toLowerCase() ===
                          visitId.toLowerCase();
                      const isBooked = Boolean(row?._booked);
                      const start = String(
                        row?.TentativeStartDate ??
                          row?.tentativeStartDate ??
                          "",
                      ).trim();
                      const end = String(
                        row?.TentativeEndDate ??
                          row?.tentativeEndDate ??
                          "",
                      ).trim();
                      const dayOffset = Number(row?.DayOffset);
                      const showDayOffset =
                        !isBooked &&
                        Number.isFinite(dayOffset) &&
                        dayOffset !== 0;
                      const hasDates = Boolean(start || end);
                      const isLast = index === sortedWindowRows.length - 1;
                      const cardClass = [
                        "eplanner-book__window-card",
                        selected ? "is-selected" : "",
                        isBooked ? "is-booked" : "",
                        isLast ? "is-last" : "",
                      ]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <li
                          key={visitId || `${meta.visitName}-${index}`}
                          className={cardClass}
                        >
                          <div className="eplanner-book__window-card-btn">
                            <span className="eplanner-book__window-rail" aria-hidden="true">
                              <span className="eplanner-book__window-dot" />
                              {!isLast ? (
                                <span className="eplanner-book__window-line" />
                              ) : null}
                            </span>
                            <span className="eplanner-book__window-card-body">
                              <span className="eplanner-book__window-card-top">
                                <span className="eplanner-book__window-name">
                                  {meta.visitName}
                                </span>
                                <span className="eplanner-book__window-tags">
                                  {showDayOffset ? (
                                    <span className="eplanner-book__window-day-tag">
                                      Day +{dayOffset}
                                    </span>
                                  ) : null}
                                  {isBooked ? (
                                    <span className="eplanner-book__window-booked-tag">
                                      Booked
                                    </span>
                                  ) : null}
                                  {selected && !isBooked ? (
                                    <span className="eplanner-book__window-now-tag">
                                      Booking
                                    </span>
                                  ) : null}
                                </span>
                              </span>
                              {hasDates ? (
                                <span className="eplanner-book__window-dates">
                                  <span className="eplanner-book__window-date-block">
                                    <span className="eplanner-book__window-date-label">
                                      Expected start
                                    </span>
                                    <span className="eplanner-book__window-date-value">
                                      {start || "—"}
                                    </span>
                                  </span>
                                  <span className="eplanner-book__window-date-sep" aria-hidden="true">
                                    →
                                  </span>
                                  <span className="eplanner-book__window-date-block">
                                    <span className="eplanner-book__window-date-label">
                                      Expected end
                                    </span>
                                    <span className="eplanner-book__window-date-value">
                                      {end || "—"}
                                    </span>
                                  </span>
                                </span>
                              ) : (
                                <span className="eplanner-book__window-pending">
                                  {meta.pendingRefLabel ||
                                    "Waiting for study, group, and date."}
                                </span>
                              )}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                ) : (
                  <div className="eplanner-book__window-empty">
                    <span className="eplanner-book__window-empty-icon" aria-hidden="true">
                      <i className="fas fa-calendar-alt" />
                    </span>
                    <p className="eplanner-book__empty">{windowHint}</p>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
  );

  return (
    <>
      {asPage ? (
        <div className="eplanner-book-page">{workspace}</div>
      ) : (
        <CtmsWorkspaceModal
          open={open}
          title="Book appointment"
          onClose={handleClose}
          dialogClassName="ctms-workspace-modal__dialog--full"
          contentClassName="eplanner-book-modal"
        >
          {workspace}
      </CtmsWorkspaceModal>
      )}
      <AppointmentLocationPicker
        open={locationPickerOpen}
        onClose={() => setLocationPickerOpen(false)}
        userData={userData}
        studyId={form.studyId}
        visitId={form.visitId}
        appointmentStartTime={toLegacyStartTime(
          form.appointmentDate,
          form.appointmentTime,
        )}
        slotDuration={timeIntervalMinutes}
        initialSelection={{
          locationId: form.locationId,
          roomTypeId: form.roomTypeId,
          roomId: form.roomId,
        }}
        onSelect={(selection) => {
          setForm((current) => ({
            ...current,
            locationId: selection?.locationId || "",
            buildingId: selection?.buildingId || "",
            floorId: selection?.floorId || "",
            roomId: selection?.roomId || "",
            roomTypeId: selection?.roomTypeId || "",
            locationType: selection?.type || "",
            locationLabel: selection?.label || "",
          }));
        }}
      />
      <InfoAlert {...alertState} onClose={hideAlert} />
    </>
  );
}
